import React, { Suspense } from 'react'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import { MODULE_NAME, ROUTES } from '../constants'
import { modules, moduleKeyByPath } from '../modules'
import Branch from '../pages/Branch'
import BranchDetail from '../pages/branchManagement/components/BranchDetail'
import DeviceDetail from '../pages/deviceManagement/Devices/DeviceDetail'
import ExternalModuleDetail from '../pages/deviceManagement/externalModules/ExternalModuleDetail'
import InputModuleDetail from '../pages/deviceManagement/inputModules/InputModuleDetail'
import VTagDetail from '../pages/deviceManagement/Vtags/VTagDetail'
import UserDetail from '../pages/userManagement/components/UserDetail'
import UsersRoles from '../pages/usersRoles/UsersRoles'

const ChangePassword = React.lazy(() => import('../pages/auth/changePassword'))
const Profile = React.lazy(() => import('../pages/profile'))

const Loader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CircularProgress />
    </Box>
  )
}

const UserManagement = React.lazy(() => import('../pages/userManagement'))
const BranchManagement = React.lazy(() => import('../pages/branchManagement'))
const Dashboard = React.lazy(() => import('../pages/dashboard'))
const Facilities = React.lazy(() => import('../pages/facilities'))
const FacilityHierarchy = React.lazy(() => import('../pages/facilities/FacilityHierarchy'))
const FacilityBlueprint = React.lazy(() => import('../pages/facilities/FacilityBlueprint'))
const ColdRooms = React.lazy(() => import('../pages/facilities/coldrooms/ColdRooms'))
const ColdRoomDetail = React.lazy(() => import('../pages/facilities/coldrooms/ColdRoomDetail'))
const ColdRoomEditor = React.lazy(() => import('../pages/facilities/coldrooms/ColdRoomEditor'))
const Sensors = React.lazy(() => import('../pages/deviceManagement'))
const AccessPointDetail = React.lazy(
  () => import('../pages/deviceManagement/AccessPoint/AccessPointDetail')
)
const Login = React.lazy(() => import('../pages/auth/login'))
const ForgotPassword = React.lazy(() => import('../pages/auth/forgotPassword'))
const Alarms = React.lazy(() => import('../pages/alarms'))
const AlarmProfileDetail = React.lazy(() => import('../pages/alarms/AlarmProfileDetail'))
const AlarmLogDetails = React.lazy(() => import('../pages/alarms/alarm-log/details'))
const CreateAlarmProfile = React.lazy(() => import('../pages/alarms/create-alarm-profile'))
const Notifications = React.lazy(() => import('../pages/notifications'))
const Reminders = React.lazy(() => import('../pages/reminders'))
const Activity = React.lazy(() => import('../pages/activity'))
const GlobalSettings = React.lazy(() => import('../pages/globalSettings'))
const Documents = React.lazy(() => import('../pages/documents'))
const Reports = React.lazy(() => import('../pages/reports'))
const NewSchedule = React.lazy(() => import('../pages/reports/NewSchedule'))
const Kpi = React.lazy(() => import('../pages/kpi'))
const Help = React.lazy(() => import('../pages/help'))
const WhatsNew = React.lazy(() => import('../pages/whatsNew'))
const ScheduleDetails = React.lazy(() => import('../pages/reports/ScheduleDetails'))
const NotFound = React.lazy(() => import('../pages/notFound'))

// Helper: build a permission-aware route entry. `routePath` is the
// key used to look the module up in the @module registry; the actual
// React-Router `path` can be different (e.g. when the URL has optional
// segments or `:param` placeholders the registry doesn't enumerate) —
// pass `path` in `options` to override. Pass `requiredModule` to bypass
// the registry lookup entirely (used for sub-routes like /coldrooms/:uuid
// that belong to a module whose registered `path` is elsewhere).
//
// Each row also carries `permissionKeys` — the full OR-fallback list
// the sidebar uses for the same module. The route guard consumes this
// (see PermissionGuard) so a user landing on /sensors sees the page if
// they hold *any* of `sensor` / `device` / `access-point` / `v-tag`,
// matching the sidebar's "show Sensors when any of those four slugs is
// granted" rule. When the registry has no permissionKeys (or the route
// has no registered path), we fall back to wrapping the single
// `requiredModule` in a one-element list so the guard still has
// something to OR over.
const guarded = (routePath, lazyElement, options = {}) => {
  const path = options.path ?? routePath
  const requiredModule = options.requiredModule ?? moduleKeyByPath[routePath] ?? null
  const registryEntry = modules[options.requiredModule ?? moduleKeyByPath[routePath]]
  let permissionKeys
  if (Array.isArray(options.permissionKeys)) {
    permissionKeys = options.permissionKeys
  } else if (Array.isArray(registryEntry?.permissionKeys)) {
    permissionKeys = registryEntry.permissionKeys
  } else if (requiredModule) {
    permissionKeys = [requiredModule]
  }
  // Pick up the `requiresCompanyType` flag from the registry so the
  // route guard mirrors the sidebar's visibility rule — Branch
  // Management, for example, is hidden in the sidebar AND redirects to
  // /dashboard if a non-COMPANY user lands on /branches via URL.
  // Callers can override via `options.requiresCompanyType` for tests
  // or one-off cases; the registry is the default source.
  const requiresCompanyType =
    typeof options.requiresCompanyType === 'boolean'
      ? options.requiresCompanyType
      : Boolean(registryEntry?.requiresCompanyType)
  return {
    path,
    requiredModule,
    permissionKeys,
    requiresCompanyType,
    component: <Suspense fallback={<Loader />}>{lazyElement}</Suspense>,
  }
}

// Account-level routes (profile, change-password, branch switcher) —
// stay accessible to every authenticated user regardless of module
// permissions, so a role downgrade can't lock the user out of recovery
// controls. Also renders no `requiredModule`.
const accountOnly = (path, lazyElement) => ({
  path,
  requiredModule: null,
  component: <Suspense fallback={<Loader />}>{lazyElement}</Suspense>,
})

export const authProtectedRoutes = [
  guarded(ROUTES.DASHBOARD, <Dashboard />),
  guarded(ROUTES.FACILITIES, <Facilities />),
  guarded('/facilities/:id', <FacilityHierarchy />, {
    requiredModule: MODULE_NAME.FACILITY,
  }),
  guarded('/facilities/:uuid/blueprint', <FacilityBlueprint />, {
    requiredModule: MODULE_NAME.FACILITY,
  }),
  guarded('/coldrooms', <ColdRooms />, { requiredModule: MODULE_NAME.FACILITY }),
  guarded('/coldrooms/:uuid', <ColdRoomDetail />, {
    requiredModule: MODULE_NAME.FACILITY,
  }),
  guarded('/coldrooms/:uuid/edit', <ColdRoomEditor />, {
    requiredModule: MODULE_NAME.FACILITY,
  }),
  guarded(ROUTES.SENSORS, <Sensors />, { path: '/sensors/:tabName?' }),
  guarded('/sensors/details/:uuid', <DeviceDetail />, {
    requiredModule: MODULE_NAME.SENSOR,
  }),
  guarded('/sensors/input-modules/details/:uuid', <InputModuleDetail />, {
    requiredModule: MODULE_NAME.SENSOR,
  }),
  guarded('/sensors/external-modules/details/:uuid', <ExternalModuleDetail />, {
    requiredModule: MODULE_NAME.SENSOR,
  }),
  guarded('/sensors/vtags/detail/:uuid', <VTagDetail />, {
    requiredModule: MODULE_NAME.SENSOR,
  }),
  guarded('/access-points/details/:uuid', <AccessPointDetail />, {
    requiredModule: MODULE_NAME.SENSOR,
  }),
  guarded(ROUTES.ROLES, <UsersRoles />, { path: '/users-roles' }),
  guarded(ROUTES.BRANCH_MANAGEMENT, <BranchManagement />),
  guarded('/branch-management/details/:uuid', <BranchDetail />, {
    requiredModule: MODULE_NAME.BRANCH,
  }),
  accountOnly('/change-password', <ChangePassword />),
  accountOnly(ROUTES.PROFILE, <Profile />),
  accountOnly(ROUTES.BRANCH, <Branch />),
  guarded(ROUTES.USER_MANAGEMENT, <UserManagement />),
  guarded('/user-management/details/:uuid', <UserDetail />, {
    requiredModule: MODULE_NAME.USER_MANAGEMENT,
  }),
  guarded(ROUTES.ALARMS, <Alarms />),
  guarded('/alarms/details/:uuid', <AlarmProfileDetail />, {
    requiredModule: MODULE_NAME.ALARMS,
  }),
  guarded('/alarms/log/details/:uuid', <AlarmLogDetails />, {
    requiredModule: MODULE_NAME.ALARMS,
  }),
  guarded('/alarms/edit/:uuid', <CreateAlarmProfile />, {
    requiredModule: MODULE_NAME.ALARMS,
  }),
  guarded(ROUTES.NOTIFICATIONS, <Notifications />),
  guarded(ROUTES.REMINDERS, <Reminders />),
  guarded(ROUTES.ACTIVITY, <Activity />),
  guarded(ROUTES.GLOBAL_SETTINGS, <GlobalSettings />),
  guarded(ROUTES.DOCUMENTS, <Documents />),
  guarded(ROUTES.REPORTS, <Reports />),
  guarded(ROUTES.REPORTS_NEW_SCHEDULE, <NewSchedule />),
  guarded(ROUTES.REPORTS_EDIT_SCHEDULE, <NewSchedule />, { path: '/reports/schedule/edit/:id' }),
  guarded(ROUTES.KPI, <Kpi />),
  guarded(ROUTES.HELP, <Help />, { path: '/help' }),
  guarded('/help/:slug', <Help />, {
    requiredModule: MODULE_NAME.HELP,
    path: '/help/:slug',
  }),
  guarded(ROUTES.WHATS_NEW, <WhatsNew />, {
    requiredModule: MODULE_NAME.WHATS_NEW,
    permissionKeys: [],
  }),
  guarded('/whats-new/:slug', <WhatsNew />, {
    requiredModule: MODULE_NAME.WHATS_NEW,
    // Detail route — `:slug` is the canonical handle the backend
    // returns for a published post. Anything inside the route tree
    // is treated as a slug segment (so far-future taxonomy slugs
    // work without an extra route row).
    permissionKeys: [],
  }),
  guarded(ROUTES.REPORTS_SCHEDULE_DETAILS, <ScheduleDetails />),
  { path: '/', exact: true, component: <></>, requiredModule: null },
  { path: '*', component: <></>, requiredModule: null },
]

export const publicRoutes = [
  {
    path: '/login',
    component: (
      <Suspense fallback={<Loader />}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    component: (
      <Suspense fallback={<Loader />}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: '*',
    component: (
      <Suspense fallback={<Loader />}>
        <NotFound />
      </Suspense>
    ),
  },
]

export { Loader }
